import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../store/slices/authSlice';
import { Toast } from '../components/Toast';
import TagModal from '../components/TagModal';

function AddPin() {
  const navigate = useNavigate();
  const currentUser = useSelector(selectCurrentUser);
  const token = useSelector((state) => state.auth.token);
  
  const [formData, setFormData] = useState({
    title: '',
    prompt: '',
    refHint: '',
    boardId: ''
  });
  
  const [errors, setErrors] = useState({
    title: '',
    prompt: '',
    image: '',
    board: ''
  });
  
  const [touched, setTouched] = useState({
    title: false,
    prompt: false,
    image: false,
    board: false
  });
  
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [boards, setBoards] = useState([]);
  const [selectedTags, setSelectedTags] = useState(new Set());
  const [showBoardDropdown, setShowBoardDropdown] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [selectedBoardName, setSelectedBoardName] = useState('Chọn bảng');
  const [selectedBoard, setSelectedBoard] = useState(null); // Lưu board object để lấy tags

  const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    loadBoards();
  }, []);

  const loadBoards = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/boards`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to load boards');
      const data = await response.json();
      setBoards(data);
    } catch (error) {
      console.error('Error loading boards:', error);
      Toast.error('Không thể tải danh sách bảng');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Validate on change if field was touched
    if (touched[name]) {
      validateField(name, value);
    }
  };

  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
    
    if (field === 'title') {
      validateField('title', formData.title);
    } else if (field === 'prompt') {
      validateField('prompt', formData.prompt);
    }
  };

  const validateField = (field, value) => {
    let error = '';
    
    switch (field) {
      case 'title':
        if (!value.trim()) {
          error = 'Tiêu đề không được để trống';
        } else if (value.trim().length < 3) {
          error = 'Tiêu đề phải có ít nhất 3 ký tự';
        } else if (value.trim().length > 100) {
          error = 'Tiêu đề không được quá 100 ký tự';
        }
        break;
      
      case 'prompt':
        if (!value.trim()) {
          error = 'Prompt không được để trống';
        } else if (value.trim().length < 10) {
          error = 'Prompt phải có ít nhất 10 ký tự';
        } else if (value.trim().length > 5000) {
          error = 'Prompt không được quá 5000 ký tự';
        }
        break;
      
      case 'board':
        if (!formData.boardId) {
          error = 'Vui lòng chọn bảng';
        }
        break;
      
      case 'image':
        if (!imageFile) {
          error = 'Vui lòng tải ảnh lên';
        }
        break;
    }
    
    setErrors(prev => ({ ...prev, [field]: error }));
    return error;
  };

  const validateForm = () => {
    const newErrors = {
      title: validateField('title', formData.title),
      prompt: validateField('prompt', formData.prompt),
      board: validateField('board', formData.boardId),
      image: validateField('image', imageFile)
    };
    
    setErrors(newErrors);
    setTouched({
      title: true,
      prompt: true,
      board: true,
      image: true
    });
    
    return !Object.values(newErrors).some(error => error !== '');
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, image: 'Vui lòng chọn file ảnh!' }));
      setTouched(prev => ({ ...prev, image: true }));
      Toast.error('Vui lòng chọn file ảnh!');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, image: 'Kích thước ảnh không được vượt quá 10MB!' }));
      setTouched(prev => ({ ...prev, image: true }));
      Toast.error('Kích thước ảnh không được vượt quá 10MB!');
      return;
    }

    try {
      // Compress image if needed
      const compressedFile = await compressImage(file);
      setImageFile(compressedFile);
      setErrors(prev => ({ ...prev, image: '' }));
      setTouched(prev => ({ ...prev, image: true }));
      
      // Preview
      const reader = new FileReader();
      reader.onload = (ev) => {
        setImagePreview(ev.target.result);
      };
      reader.readAsDataURL(compressedFile);
    } catch (error) {
      console.error('Error processing image:', error);
      setErrors(prev => ({ ...prev, image: 'Lỗi khi xử lý ảnh' }));
      Toast.error('Lỗi khi xử lý ảnh');
    }
  };

  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Resize if too large
          const maxDimension = 1920;
          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = (height / width) * maxDimension;
              width = maxDimension;
            } else {
              width = (width / height) * maxDimension;
              height = maxDimension;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              resolve(new File([blob], file.name, { type: 'image/jpeg' }));
            },
            'image/jpeg',
            0.85
          );
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const selectBoard = (board) => {
    setFormData({ ...formData, boardId: board._id });
    setSelectedBoardName(board.name);
    setSelectedBoard(board); // Lưu board object
    setShowBoardDropdown(false);
    setErrors(prev => ({ ...prev, board: '' }));
    setTouched(prev => ({ ...prev, board: true }));
    
    // Clear tags khi đổi board
    setSelectedTags(new Set());
  };

  const handleTagsConfirm = (tags) => {
    setSelectedTags(tags);
    setShowTagModal(false);
  };

  const removeTag = (tag) => {
    const newTags = new Set(selectedTags);
    newTags.delete(tag);
    setSelectedTags(newTags);
  };

  const handlePublish = async () => {
    console.log('🎯 handlePublish called');
    
    // Validate form
    if (!validateForm()) {
      Toast.error('Vui lòng kiểm tra lại thông tin!');
      return;
    }

    setIsPublishing(true);
    console.log('🚀 Starting publish process...');

    try {
      // Convert image to base64
      const reader = new FileReader();
      reader.readAsDataURL(imageFile);
      
      reader.onload = async () => {
        try {
          const imageBase64 = reader.result;
          console.log('📸 Image converted to base64, size:', imageBase64.length);

          // Create pin
          const pinData = {
            title: formData.title.trim(),
            prompt: formData.prompt.trim(),
            refHint: formData.refHint.trim(),
            image: imageBase64,
            board: formData.boardId,
            tags: Array.from(selectedTags),
            user: currentUser.username
          };

          console.log('📦 Sending pin data:', { 
            ...pinData, 
            image: `base64 (${imageBase64.length} chars)`,
            tags: pinData.tags,
            board: pinData.board
          });

          const url = `${API_BASE_URL}/pins`;
          console.log('🌐 API URL:', url);

          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(pinData)
          });

          console.log('📡 Response status:', response.status);
          console.log('📡 Response ok:', response.ok);

          const responseText = await response.text();
          console.log('📄 Response text:', responseText);

          if (!response.ok) {
            let error;
            try {
              error = JSON.parse(responseText);
            } catch (e) {
              error = { error: responseText };
            }
            console.error('❌ Error response:', error);
            throw new Error(error.error || 'Failed to create pin');
          }

          const result = JSON.parse(responseText);
          console.log('✅ Pin created successfully:', result);

          Toast.success('Đã đăng thành công! Prompt của bạn đang chờ admin duyệt.');
          setTimeout(() => {
            navigate('/profile');
          }, 2000);
        } catch (error) {
          console.error('❌ Error in reader.onload:', error);
          console.error('❌ Error stack:', error.stack);
          Toast.error('Lỗi khi đăng: ' + error.message);
          setIsPublishing(false);
        }
      };

      reader.onerror = (error) => {
        console.error('❌ Error reading file:', error);
        Toast.error('Lỗi khi đọc file ảnh');
        setIsPublishing(false);
      };
    } catch (error) {
      console.error('❌ Error publishing:', error);
      console.error('❌ Error stack:', error.stack);
      Toast.error('Lỗi khi đăng: ' + error.message);
      setIsPublishing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header Bar */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-3 flex items-center justify-between sticky top-0 z-10">
        <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">Tạo Prompt</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePublish}
            disabled={isPublishing}
            className="px-4 sm:px-6 py-2 sm:py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
          >
            {isPublishing ? 'Đang đăng...' : 'Đăng'}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8 pb-16 sm:pb-24 lg:pb-32">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="grid md:grid-cols-2 gap-0 min-h-[500px] sm:min-h-[600px] lg:min-h-[700px]">
            
            {/* Left: Image Upload */}
            <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 dark:bg-gray-900 border-b md:border-b-0 md:border-r border-gray-200 dark:border-gray-700">
              <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
                Ảnh <span className="text-red-500">*</span>
              </label>
              <label
                htmlFor="image-upload"
                className={`block relative w-full aspect-[3/4] rounded-xl border-2 border-dashed ${
                  errors.image && touched.image
                    ? 'border-red-500'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                } bg-white dark:bg-gray-800 cursor-pointer transition-all overflow-hidden`}
              >
                {imagePreview ? (
                  <div className="relative w-full h-full">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        removeImage();
                      }}
                      className="absolute top-2 right-2 w-10 h-10 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-all shadow-lg"
                    >
                      <span className="material-symbols-rounded text-[20px]">close</span>
                    </button>
                  </div>
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 sm:gap-3 text-gray-400 dark:text-gray-500">
                    <span className="material-symbols-rounded text-4xl sm:text-5xl">cloud_upload</span>
                    <p className="font-semibold text-sm sm:text-base">Tải ảnh lên</p>
                    <p className="text-xs sm:text-sm">Kéo thả hoặc nhấp</p>
                  </div>
                )}
              </label>
              <input
                type="file"
                id="image-upload"
                className="hidden"
                accept="image/*"
                onChange={handleImageChange}
              />
              {errors.image && touched.image && (
                <p className="mt-2 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                  <span className="material-symbols-rounded text-[14px]">error</span>
                  {errors.image}
                </p>
              )}
            </div>

            {/* Right: Form */}
            <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6 bg-white dark:bg-gray-800">
              
              {/* Title */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
                  Tiêu đề <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  onBlur={() => handleBlur('title')}
                  maxLength={100}
                  placeholder="Thêm tiêu đề"
                  className={`w-full text-2xl font-semibold border-0 border-b-2 ${
                    errors.title && touched.title
                      ? 'border-red-500 focus:border-red-600'
                      : 'border-gray-200 dark:border-gray-700 focus:border-gray-900 dark:focus:border-gray-300'
                  } py-2 bg-transparent text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none transition-colors`}
                />
                <div className="flex items-center justify-between mt-1">
                  {errors.title && touched.title ? (
                    <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                      <span className="material-symbols-rounded text-[14px]">error</span>
                      {errors.title}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 dark:text-gray-400">Tối thiểu 3 ký tự</p>
                  )}
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formData.title.length}/100
                  </p>
                </div>
              </div>

              {/* Prompt */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
                  Prompt Gemini <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="prompt"
                  value={formData.prompt}
                  onChange={handleChange}
                  onBlur={() => handleBlur('prompt')}
                  maxLength={5000}
                  rows="4"
                  placeholder="Nhập câu lệnh bạn đã dùng..."
                  className={`w-full text-sm resize-none border ${
                    errors.prompt && touched.prompt
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
                  } rounded-lg px-4 py-3 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 transition-all`}
                />
                <div className="flex items-center justify-between mt-1">
                  {errors.prompt && touched.prompt ? (
                    <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                      <span className="material-symbols-rounded text-[14px]">error</span>
                      {errors.prompt}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 dark:text-gray-400">Tối thiểu 10 ký tự</p>
                  )}
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formData.prompt.length}/5000
                  </p>
                </div>
              </div>

              {/* Reference Hint */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
                  Gợi ý ảnh cần đưa vào
                </label>
                <textarea
                  name="refHint"
                  value={formData.refHint}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Ví dụ: Ảnh selfie rõ mặt, phông nền sáng..."
                  className="w-full text-sm resize-none border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-3 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Board Selection */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
                  Bảng <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowBoardDropdown(!showBoardDropdown)}
                    className={`w-full flex items-center justify-between px-4 py-3 border ${
                      errors.board && touched.board
                        ? 'border-red-500'
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                    } rounded-lg transition-colors bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-left`}
                  >
                    <span className="flex items-center gap-2">
                      {selectedBoard && <span className="text-xl">{selectedBoard.icon}</span>}
                      <span className={!selectedBoard ? 'text-gray-400 dark:text-gray-500' : ''}>
                        {selectedBoardName}
                      </span>
                    </span>
                    <span className="material-symbols-rounded text-gray-400 dark:text-gray-500">expand_more</span>
                  </button>
                  {errors.board && touched.board && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                      <span className="material-symbols-rounded text-[14px]">error</span>
                      {errors.board}
                    </p>
                  )}
                  
                  {showBoardDropdown && (
                    <div className="absolute left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-50 max-h-[400px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">
                      {boards.length === 0 ? (
                        <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                          <span className="material-symbols-rounded text-4xl mb-2 block">folder_off</span>
                          <p className="text-sm">Không có bảng nào</p>
                        </div>
                      ) : (
                        <div className="py-2">
                          {boards.map((board, index) => (
                            <div
                              key={board._id}
                              onClick={() => selectBoard(board)}
                              className={`px-4 py-3 mx-2 mb-2 rounded-lg hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 dark:hover:from-gray-700 dark:hover:to-gray-600 cursor-pointer transition-all ${
                                index === boards.length - 1 ? 'mb-0' : ''
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                <span className="text-2xl flex-shrink-0">{board.icon || '📌'}</span>
                                <div className="flex-1 min-w-0">
                                  <div className="font-semibold text-gray-900 dark:text-white mb-0.5">{board.name}</div>
                                  {board.description && (
                                    <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">{board.description}</div>
                                  )}
                                  {board.tags && board.tags.length > 0 && (
                                    <div className="text-xs text-blue-600 dark:text-blue-400 mt-1 font-medium">
                                      {board.tags.length} tags
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
                  Thẻ
                </label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {Array.from(selectedTags).map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 dark:bg-gray-700 text-white text-sm rounded-full"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="hover:text-gray-300"
                      >
                        <span className="material-symbols-rounded text-sm">close</span>
                      </button>
                    </span>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setShowTagModal(true)}
                  disabled={!selectedBoard}
                  className={`w-full flex items-center justify-start gap-2 px-4 py-3 border rounded-lg transition-colors ${
                    !selectedBoard
                      ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 cursor-not-allowed opacity-60'
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 bg-white dark:bg-gray-900'
                  }`}
                  title={!selectedBoard ? 'Vui lòng chọn bảng trước' : ''}
                >
                  <span className="material-symbols-rounded text-gray-400 dark:text-gray-500">add</span>
                  <span className="text-gray-700 dark:text-gray-300">
                    {!selectedBoard ? 'Chọn bảng trước để thêm thẻ' : 'Thêm thẻ'}
                  </span>
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Tag Modal */}
      {showTagModal && selectedBoard && (
        <TagModal
          selectedTags={selectedTags}
          onClose={() => setShowTagModal(false)}
          onConfirm={handleTagsConfirm}
          availableTags={selectedBoard.tags || []}
          boardName={selectedBoard.name}
        />
      )}

      {/* Click outside to close dropdown */}
      {showBoardDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowBoardDropdown(false)}
        />
      )}
    </div>
  );
}

export default AddPin;
